import { supabase } from '../lib/supabase'
import type { Campaign } from '../types/index'

function rowToCampaign(r: Record<string, unknown>): Campaign {
  return {
    id:               r.id as string,
    name:             r.name as string,
    clientId:         (r.client_id as string) || '',
    clientName:       (r.client_name as string) || '',
    type:             (r.type as Campaign['type']) || 'Google Ads',
    status:           (r.status as Campaign['status']) || 'active',
    budget:           (r.budget as number) || 0,
    spend:            (r.spend as number) || 0,
    impressions:      (r.impressions as number) || 0,
    clicks:           (r.clicks as number) || 0,
    conversions:      (r.conversions as number) || 0,
    revenue:          (r.revenue as number) || 0,
    roas:             (r.roas as number) || 0,
    startDate:        (r.start_date as string) || '',
    endDate:          r.end_date as string | undefined,
    metaCampaignId:   r.meta_campaign_id as string | undefined,
  }
}

export async function getCampaigns(): Promise<Campaign[]> {
  const { data, error } = await supabase.from('campaigns').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(rowToCampaign)
}

export async function createCampaign(c: Omit<Campaign, 'id'>): Promise<Campaign> {
  const { data, error } = await supabase.from('campaigns').insert({
    name: c.name, client_id: c.clientId || null, client_name: c.clientName,
    type: c.type, status: c.status, budget: c.budget, spend: c.spend,
    impressions: c.impressions, clicks: c.clicks, conversions: c.conversions,
    revenue: c.revenue, roas: c.roas, start_date: c.startDate, end_date: c.endDate,
    ...(c.metaCampaignId ? { meta_campaign_id: c.metaCampaignId } : {}),
  }).select().single()
  if (error) throw error
  return rowToCampaign(data)
}

export async function upsertMetaCampaign(c: Omit<Campaign, 'id'>): Promise<Campaign> {
  const { data, error } = await supabase
    .from('campaigns')
    .upsert({
      name: c.name, client_id: c.clientId || null, client_name: c.clientName,
      type: 'Meta Ads', status: c.status, budget: c.budget, spend: c.spend,
      impressions: c.impressions, clicks: c.clicks, conversions: c.conversions,
      revenue: c.revenue, roas: c.roas, start_date: c.startDate, end_date: c.endDate || null,
      meta_campaign_id: c.metaCampaignId,
    }, { onConflict: 'meta_campaign_id', ignoreDuplicates: false })
    .select().single()
  if (error) throw error
  return rowToCampaign(data)
}

export async function updateCampaign(id: string, c: Partial<Campaign>): Promise<void> {
  const updates: Record<string, unknown> = {}
  if (c.name        !== undefined) updates.name        = c.name
  if (c.clientId    !== undefined) updates.client_id   = c.clientId || null
  if (c.clientName  !== undefined) updates.client_name = c.clientName
  if (c.type        !== undefined) updates.type        = c.type
  if (c.status      !== undefined) updates.status      = c.status
  if (c.budget      !== undefined) updates.budget      = c.budget
  if (c.spend       !== undefined) updates.spend       = c.spend
  if (c.impressions !== undefined) updates.impressions = c.impressions
  if (c.clicks      !== undefined) updates.clicks      = c.clicks
  if (c.conversions !== undefined) updates.conversions = c.conversions
  if (c.revenue     !== undefined) updates.revenue     = c.revenue
  if (c.roas        !== undefined) updates.roas        = c.roas
  if (c.startDate        !== undefined) updates.start_date       = c.startDate
  if (c.endDate          !== undefined) updates.end_date         = c.endDate
  if (c.metaCampaignId   !== undefined) updates.meta_campaign_id = c.metaCampaignId
  const { error } = await supabase.from('campaigns').update(updates).eq('id', id)
  if (error) throw error
}

export async function deleteCampaign(id: string): Promise<void> {
  const { error } = await supabase.from('campaigns').delete().eq('id', id)
  if (error) throw error
}
